// /admin/demo-stats — overzichtspagina voor demo-statistieken (fictief, anoniem).
import React, { useCallback, useEffect, useState } from 'react';
import Icon from './Icon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = '#0e2340';
const GOLD = '#c48a3a';

const st = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #0e2340, #050a14)', padding: '32px 20px 60px' },
  wrap: { maxWidth: 860, margin: '0 auto' },
  card: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 18, padding: 18 },
  eyebrow: { color: GOLD, fontSize: 10, fontWeight: 900, letterSpacing: 1.4 },
  h: { color: '#fff', fontSize: 17, fontWeight: 900, marginBottom: 12 },
  muted: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
};

function Kpi({ label, value, sub }) {
  return (
    <div style={{ ...st.card, flex: 1, minWidth: 150 }}>
      <div style={st.eyebrow}>{label}</div>
      <div style={{ color: '#fff', fontSize: 30, fontWeight: 900, marginTop: 6 }}>{value}</div>
      {sub ? <div style={{ ...st.muted, marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}

function Funnel({ title, steps }) {
  const max = Math.max(...steps.map((s) => s[1]), 1);
  return (
    <div style={st.card}>
      <div style={st.h}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {steps.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, width: 190, flexShrink: 0 }}>{label}</span>
            <div style={{ flex: 1, height: 18, borderRadius: 9, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{ height: 18, borderRadius: 9, background: `linear-gradient(90deg, ${GOLD}, #e9c88a)`, width: `${Math.round((value / max) * 100)}%`, minWidth: value ? 18 : 0, transition: 'width .4s ease' }} />
            </div>
            <span style={{ color: GOLD, fontSize: 12.5, fontWeight: 900, width: 34, textAlign: 'right' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DemoStats() {
  const [summary, setSummary] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      fetch(`${API}/demo/track/summary`).then((r) => r.json()),
      fetch(`${API}/analytics/demo-summary`).then((r) => r.json()),
    ])
      .then(([s, l]) => { setSummary(s); setLegacy(l); setError(null); setRefreshedAt(new Date().toLocaleTimeString('nl-BE')); })
      .catch(() => setError('Kon de statistieken niet laden.'));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  return (
    <div style={st.page} data-testid="demo-stats-page">
      <div style={st.wrap}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 900, letterSpacing: 1.1 }}>
              IMMO<span style={{ color: GOLD, fontStyle: 'italic', fontWeight: 400 }}>match</span>{' '}
              <span style={{ color: GOLD, fontSize: 9, fontWeight: 900, letterSpacing: 1.3 }}>DEMO · STATISTIEKEN</span>
            </div>
            <div style={{ ...st.muted, marginTop: 3 }}>Anonieme demo-events · fictieve omgeving {refreshedAt ? `· ververst ${refreshedAt}` : ''}</div>
          </div>
          <button className="immo-btn" data-testid="stats-refresh" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 7, background: GOLD, borderRadius: 999, padding: '10px 18px' }}>
            <Icon name="rotate-ccw" size={14} color={NAVY} />
            <span style={{ color: NAVY, fontSize: 12.5, fontWeight: 900 }}>Vernieuwen</span>
          </button>
        </div>

        {error ? <div style={{ ...st.card, color: '#ffb4ab', fontSize: 13, marginBottom: 14 }} data-testid="stats-error">{error}</div> : null}
        {!summary && !error ? <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Laden…</div> : null}

        {summary ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Kpi label="TOTALE EVENTS" value={summary.total_events} sub="alle demo-interacties" />
              <Kpi label="UNIEKE SESSIES" value={summary.unique_sessions} sub="browsertabs met demo" />
              <Kpi label="CTA-KLIKKEN" value={legacy?.total ?? 0} sub={legacy ? `website ${legacy.per_source.website} · cta ${legacy.per_source.cta} · qr ${legacy.per_source.qr}` : ''} />
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 320 }}>
                <Funnel title="IJsberg-funnel" steps={[
                  ['demo gestart', summary.iceberg_funnel.demo_started],
                  ['ijsberg geopend', summary.iceberg_funnel.iceberg_opened],
                  ['laag onthuld', summary.iceberg_funnel.iceberg_layer_revealed],
                  ['ijsberg voltooid', summary.iceberg_funnel.iceberg_completed],
                  ['CTA geklikt', summary.iceberg_funnel.app_cta_clicked],
                ]} />
              </div>
              <div style={{ flex: 1, minWidth: 320 }}>
                <Funnel title="App-demo-funnel" steps={[
                  ['demo gestart', summary.app_demo_funnel.demo_started],
                  ['modus gekozen', summary.app_demo_funnel.mode_selected],
                  ['match bekeken', summary.app_demo_funnel.match_open],
                  ['woning bewaard', summary.app_demo_funnel.property_saved],
                  ['ijsberg', summary.app_demo_funnel.iceberg_open],
                  ['woonreis', summary.app_demo_funnel.woonreis_open],
                  ['wooncoach', summary.app_demo_funnel.wooncoach_open],
                  ['partners', summary.app_demo_funnel.partners_open],
                  ['einde bereikt', summary.app_demo_funnel.end_open],
                  ['CTA geklikt', summary.app_demo_funnel.app_cta_clicked],
                ]} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ ...st.card, flex: 1, minWidth: 260 }}>
                <div style={st.h}>Sessie-verdeling</div>
                {[
                  ['Enkel ijsberg', summary.sessions_split.iceberg_only],
                  ['App-demo · solo', summary.sessions_split.app_demo_solo],
                  ['App-demo · koppel', summary.sessions_split.app_demo_couple],
                  ['Zonder modus', summary.sessions_split.app_demo_no_mode],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12.5 }}>{l}</span>
                    <span style={{ color: '#fff', fontSize: 12.5, fontWeight: 800 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...st.card, flex: 1, minWidth: 260 }}>
                <div style={st.h}>Events per type</div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {Object.entries(summary.per_event).sort((a, b) => b[1] - a[1]).map(([ev, n]) => (
                    <div key={ev} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, fontFamily: 'Menlo, monospace' }}>{ev}</span>
                      <span style={{ color: GOLD, fontSize: 11.5, fontWeight: 800 }}>{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={st.card}>
              <div style={st.h}>Recente sessies</div>
              {summary.recent_sessions.length === 0 ? <div style={st.muted}>Nog geen sessies.</div> : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {summary.recent_sessions.map((s) => (
                  <div key={s.session_id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.07)' }} data-testid={`session-${s.session_id.slice(0, 8)}`}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: '#fff', fontSize: 11, fontFamily: 'Menlo, monospace' }}>{s.session_id.slice(0, 8)}…</span>
                      {s.mode ? <span style={{ background: 'rgba(196,138,58,0.15)', color: GOLD, fontSize: 9.5, fontWeight: 900, padding: '2px 8px', borderRadius: 999 }}>{s.mode.toUpperCase()}</span> : null}
                      {s.contexts.map((ctx) => (
                        <span key={ctx} style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: 9.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{ctx}</span>
                      ))}
                      {s.reached_end ? <span style={{ color: '#4ade80', fontSize: 10, fontWeight: 800 }}>✓ einde</span> : null}
                      {s.reached_cta ? <span style={{ color: '#4ade80', fontSize: 10, fontWeight: 800 }}>✓ CTA</span> : null}
                      <span style={{ ...st.muted, marginLeft: 'auto', fontSize: 10.5 }}>{s.event_count} events · {s.last_seen ? new Date(s.last_seen).toLocaleString('nl-BE') : ''}</span>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, fontFamily: 'Menlo, monospace', marginTop: 6, lineHeight: '16px', wordBreak: 'break-word' }}>
                      {s.trail.join(' → ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
